import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Lock, Eye, EyeOff, Loader2, Check, X } from "lucide-react";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { ICON_SIZES } from "@/lib/iconStandards";
import { Pill, PrimaryCTA, AmbientBackdrop } from "@/components/ui/atrium";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "signin";
  const { toast } = useToast();

  const requirements = [
    { label: "At least 8 characters", test: (pwd: string) => pwd.length >= 8 },
    { label: "Contains uppercase letter", test: (pwd: string) => /[A-Z]/.test(pwd) },
    { label: "Contains lowercase letter", test: (pwd: string) => /[a-z]/.test(pwd) },
    { label: "Contains a number", test: (pwd: string) => /\d/.test(pwd) },
    { label: "Contains a symbol", test: (pwd: string) => /[^a-zA-Z0-9]/.test(pwd) },
  ];

  const getRequirementStatus = (requirement: { test: (pwd: string) => boolean }) => {
    if (!password) return null;
    return requirement.test(password);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) navigate(`/auth?mode=${mode}`);
    });
  }, [navigate, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast({ title: "Password updated", description: "Your password has been successfully reset." });
      navigate(`/auth?mode=${mode}`);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground flex items-center justify-center p-4 md:p-6 lg:p-8 overflow-hidden">
      <AmbientBackdrop variant="hero" />
      <div className="relative w-full max-w-lg rounded-3xl border border-border/50 bg-card/85 backdrop-blur-sm p-8 md:p-10 shadow-[0_18px_42px_-18px_rgba(62,92,77,0.25)]">
        <div className="text-center mb-8 space-y-4">
          <div className="flex justify-center"><Pill>Reset</Pill></div>
          <h1 className="font-sans font-medium text-3xl md:text-4xl tracking-[-0.025em] text-foreground">
            Reset your <span className="font-serif italic font-light text-primary">password</span>
          </h1>
          <p className="text-foreground/70">Enter your new password below.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-2xl border border-border/50 bg-background/60 backdrop-blur-sm p-4 space-y-2">
            <Pill>Password must contain</Pill>
            <ul className="space-y-1.5 pt-2">
              {requirements.map((req) => {
                const status = getRequirementStatus(req);
                return (
                  <li key={`req-${req.label}`} className="flex items-center gap-2 text-sm">
                    {status === true && <Check className={`${ICON_SIZES.sm} text-primary flex-shrink-0`} />}
                    {status === false && <X className={`${ICON_SIZES.sm} text-destructive flex-shrink-0`} />}
                    {status === null && <div className="h-4 w-4 rounded-full border border-border/60 flex-shrink-0" />}
                    <span className={status === true ? "text-foreground" : "text-foreground/65"}>
                      {req.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-medium text-foreground/75">
              New password
            </Label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 ${ICON_SIZES.sm} text-foreground/55`} />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9 pr-9 h-12 rounded-2xl bg-background/80 border-border/60 focus-visible:ring-primary/30"
                disabled={loading}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/55 hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className={ICON_SIZES.sm} /> : <Eye className={ICON_SIZES.sm} />}
              </button>
            </div>
            {password && <PasswordStrengthIndicator password={password} />}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-xs font-medium text-foreground/75">
              Confirm password
            </Label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 ${ICON_SIZES.sm} text-foreground/55`} />
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-9 h-12 rounded-2xl bg-background/80 border-border/60 focus-visible:ring-primary/30"
                disabled={loading}
                required
                minLength={6}
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <PrimaryCTA type="submit" disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className={`${ICON_SIZES.sm} animate-spin`} />
                Updating password...
              </>
            ) : (
              "Reset password"
            )}
          </PrimaryCTA>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
